<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="800px"
  >
    <template slot="dialogTitle">
      <div>
        <span v-if="yachtModelDirty.id">
          Edit Yacht Model
        </span>
        <span v-else>
          Add New Yacht Model
        </span>
      </div>
      <div class="mt-1">
        <v-switch
          v-model="yachtModelDirty.status"
          :true-value="yachtModelStatuses.STATUS_ACTIVE"
          :false-value="yachtModelStatuses.STATUS_DRAFT"
          :disabled="!canActivateYachtModel"
          hide-details
          label="Published - selectable throughout the system (filters, lists)"
          class="caption ma-0 shrink"
        />

        <v-switch
          v-model="yachtModelDirty.is_sales_active"
          :disabled="!canActivateYachtModel || yachtModelDirty.status !== yachtModelStatuses.STATUS_ACTIVE"
          hide-details
          label="Sales Active - shows in official pricelist and selectable for new yacht on business"
          class="caption ma-0 shrink"
        />

        <v-switch
          v-model="yachtModelDirty.is_partners_visible"
          hide-details
          label="Visible to partner sales"
          class="caption ma-0 shrink"
        />
      </div>
    </template>

    <v-card-text :class="{'pa-8': !$vuetify.breakpoint.xsOnly }">
      <!--<editor-fold desc="Basic Details">-->
      <v-subheader>
        Basic Details
      </v-subheader>
      <v-select
        v-model="yachtModelDirty.yacht_type_id"
        v-validate="'required'"
        :items="yachtTypes.data"
        :loading="yachtTypes.loading"
        name="yacht_type_id"
        item-value="id"
        item-text="label"
        label="Yacht Type"
        outlined
        data-vv-as="yacht type"
        :error-messages="veeErrors.collect('yacht_type_id')"
      />
      <v-autocomplete
        v-model="yachtModelDirty.yacht_manufacturer_id"
        v-validate="'required'"
        :items="yachtManufacturers.data"
        :loading="yachtManufacturers.loading"
        name="yacht_manufacturer_id"
        item-value="id"
        item-text="name"
        label="Manufacturer"
        outlined
        data-vv-as="yacht manufacturer"
        :error-messages="veeErrors.collect('yacht_manufacturer_id')"
      />
      <v-text-field
        v-model="yachtModelDirty.name"
        v-validate="'required'"
        name="name"
        label="Official Name"
        placeholder="e.g. Sun Odyssey"
        hint="Will be used for public documents"
        outlined
        data-vv-as="official name"
        :error-messages="veeErrors.collect('name')"
      />
      <v-text-field
        v-model="yachtModelDirty.label"
        v-validate="{ required: validationRequired }"
        name="label"
        label="Internal Label"
        placeholder="e.g. Sun Odyssey (for Croatia)"
        hint="Will be used internally and for more precise identification"
        outlined
        data-vv-as="internal label"
        :error-messages="veeErrors.collect('label')"
      />
      <year-select
        v-model="yachtModelDirty.model_year"
        v-validate="{ required: validationRequired }"
        name="model_year"
        label="Model Year"
        placeholder="e.g. 2009"
        outlined
        data-vv-as="model year"
        :error-messages="veeErrors.collect('model_year')"
      />
      <!--</editor-fold>-->

      <!--<editor-fold desc="Pricing">-->
      <v-subheader v-if="$can('edit-purchase-price', 'YachtModel') || $can('edit-sale-price', 'YachtModel')">
        Pricing
        <span class="caption grey--text ml-2">
          Only basic pricing without any additional equipment, commission, delivery or registration costs
        </span>
      </v-subheader>
      <div>
        <v-row
          v-if="$can('edit-purchase-price', 'YachtModel') && $can('edit-sale-price', 'YachtModel')"
          class="list--bordered grey--text text--darken-1 pb-4 mb-4"
          align="center"
          dense
        >
          <v-col cols="auto">
            Apply
          </v-col>
          <v-col>
            <v-text-field
              v-model="automaticMarkup.percentage"
              outlined
              class="v-text-field--no-border body-1"
              placeholder="0"
              type="number"
              flat
              suffix="%"
              hide-details
              single-line
              dense
            />
          </v-col>
          <v-col>
            <v-select
              v-model="automaticMarkup.type"
              outlined
              class="v-text-field--no-border body-1"
              single-line
              hide-details
              dense
              :items="[ { value: 'margin', text: 'Margin' }, { value: 'addon', text: 'Addon' } ]"
            />
          </v-col>
          <v-col cols="auto">
            upon changing
          </v-col>
          <v-col
            cols="auto"
          >
            <v-switch
              v-model="automaticMarkup.purchaseActive"
              hide-details
              single-line
              class="pa-0 ma-0"
            />
          </v-col>
          <v-col cols="auto">
            Purchase price
          </v-col>
          <v-col
            cols="auto"
          >
            <v-switch
              v-model="automaticMarkup.saleActive"
              hide-details
              single-line
              class="pa-0 ma-0"
            />
          </v-col>
          <v-col cols="auto">
            Sale price
          </v-col>
        </v-row>

        <v-row
          v-if="$can('edit-purchase-price', 'YachtModel')"
          dense
        >
          <v-col
            cols="12"
          >
            <currency-input
              v-model="yachtModelDirty.purchase_price"
              v-validate="{ required: validationRequired, decimal: true }"
              :class="{ 'font-weight-bold': purchasePriceDiffers }"
              :error="purchasePriceDiffers"
              :precision="0"
              name="purchase_price"
              label="Purchase Price"
              placeholder="e.g. 200.000"
              outlined
              data-vv-as="purchase price"
              :error-messages="veeErrors.collect('purchase_price')"
              @input="calculateSalePrice()"
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="yachtModelDirty.currency"
              outlined
              single-line
              :items="currencyTypes"
            />
          </v-col>
        </v-row>
        <v-row
          v-if="$can('edit-sale-price', 'YachtModel')"
          dense
        >
          <v-col
            cols="12"
          >
            <currency-input
              v-model="yachtModelDirty.sale_price"
              v-validate="{ required: validationRequired, decimal: true }"
              :precision="0"
              name="sale_price"
              label="Sale Price"
              placeholder="e.g. 300.000"
              outlined
              data-vv-as="sale price"
              :error-messages="veeErrors.collect('sale_price')"
              @input="calculatePurchasePrice()"
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="yachtModelDirty.currency"
              outlined
              single-line
              :items="currencyTypes"
            />
          </v-col>
        </v-row>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Delivery Countries">-->
        <v-subheader>
          Delivery Countries
          <span class="caption grey--text ml-2">
            Countries which this model can be sold to with specified data
          </span>
        </v-subheader>
        <v-autocomplete
          v-model="yachtModelDirty.countries"
          v-validate="{ required: validationRequired }"
          :items="countries.data"
          :loading="countries.loading"
          name="countries"
          item-value="id"
          item-text="name"
          label="Select countries"
          autocomplete="disable"
          outlined
          single-line
          multiple
          small-chips
          deletable-chips
          return-object
          data-vv-as="delivery countries"
          :error-messages="veeErrors.collect('countries')"
        />
        <countries-list
          :countries="yachtModelDirty.countries"
          :currency="yachtModelDirty.currency"
          class="mb-6 mt-4"
          @remove:country="removeSelectedCountry"
        />
        <!--</editor-fold>-->

        <!--<editor-fold desc="Description / Notes">-->
        <v-subheader>Description / Notes</v-subheader>
        <v-textarea
          v-model="yachtModelDirty.description"
          outlined
          single-line
          placeholder="Enter description..."
        />
        <!--</editor-fold>-->

        <!--<editor-fold desc="Specifications">-->
        <v-subheader class="justify-space-between pr-0">
          Specifications
        </v-subheader>
        <v-row>
          <v-col
            cols="4"
          >
            <v-text-field
              v-model="yachtModelDirty.cabin_count"
              v-validate="'numeric'"
              name="cabin_count"
              data-vv-as="cabin count"
              :error-messages="veeErrors.collect('cabin_count')"
              data
              outlined
              hide-details
              label="Guest Cabin Count"
              placeholder="e.g. 4"
            />
          </v-col>
          <v-col
            cols="4"
          >
            <v-text-field
              v-model="yachtModelDirty.berth_count"
              v-validate="'numeric'"
              name="berth_count"
              data-vv-as="berth count"
              :error-messages="veeErrors.collect('berth_count')"
              outlined
              hide-details
              label="RNOG (nr. of guests)"
              placeholder="e.g. 10"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="yachtModelDirty.crew_cabin_count"
              v-validate="'numeric'"
              name="crew_cabin_count"
              data-vv-as="crew cabin count"
              :error-messages="veeErrors.collect('crew_cabin_count')"
              outlined
              hide-details
              label="Crew Cabin Count"
              placeholder="e.g. 1"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="yachtModelDirty.crew_berth_count"
              v-validate="'numeric'"
              name="crew_berth_count"
              data-vv-as="crew berth count"
              :error-messages="veeErrors.collect('crew_berth_count')"
              outlined
              hide-details
              label="Crew Berth Count"
              placeholder="e.g. 2"
            />
          </v-col>
          <v-col
            cols="4"
          >
            <currency-input
              v-model="yachtModelDirty.length"
              v-validate="'decimal'"
              name="length"
              data-vv-as="length"
              :error-messages="veeErrors.collect('length')"
              outlined
              hide-details
              label="Length"
              suffix="m"
              placeholder="e.g. 12"
            />
          </v-col>
          <v-col
            cols="4"
          >
            <currency-input
              v-model="yachtModelDirty.beam"
              v-validate="'decimal'"
              name="beam"
              data-vv-as="beam"
              :error-messages="veeErrors.collect('beam')"
              outlined
              hide-details
              label="Beam Length"
              suffix="m"
              placeholder="e.g. 10"
            />
          </v-col>
          <v-col
            cols="4"
          >
            <currency-input
              v-model="yachtModelDirty.draught"
              v-validate="'decimal'"
              name="draught"
              data-vv-as="draught"
              :error-messages="veeErrors.collect('draught')"
              outlined
              hide-details
              label="Draught"
              suffix="m"
              placeholder="e.g. 3"
            />
          </v-col>
          <v-col
            cols="4"
          >
            <currency-input
              v-model="yachtModelDirty.mast_height"
              v-validate="'decimal'"
              name="mast_height"
              data-vv-as="mast height"
              :error-messages="veeErrors.collect('mast_height')"
              outlined
              hide-details
              label="Mast Height"
              suffix="m"
              placeholder="e.g. 10"
            />
          </v-col>
          <v-col
            cols="4"
          >
            <currency-input
              v-model="yachtModelDirty.displacement"
              v-validate="'decimal'"
              name="displacement"
              data-vv-as="mast displacement"
              :error-messages="veeErrors.collect('displacement')"
              outlined
              hide-details
              label="Displacement"
              suffix="kg"
              placeholder="e.g. 1000"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="yachtModelDirty.toilette_count"
              v-validate="'numeric'"
              name="toilette_count"
              data-vv-as="toilette count"
              :error-messages="veeErrors.collect('toilette_count')"
              outlined
              hide-details
              label="Toilette Count"
              placeholder="e.g. 2"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="yachtModelDirty.steering_wheel_count"
              v-validate="'numeric'"
              name="steering_wheel_count"
              data-vv-as="steering wheel count"
              :error-messages="veeErrors.collect('steering_wheel_count')"
              outlined
              hide-details
              label="Steering Wheel Count"
              placeholder="e.g. 1"
            />
          </v-col>
          <v-col cols="4">
            <currency-input
              v-model="yachtModelDirty.main_sail_area"
              v-validate="'decimal'"
              name="main_sail_area"
              data-vv-as="main sail area"
              :error-messages="veeErrors.collect('main_sail_area')"
              outlined
              hide-details
              label="Sail area main"
              placeholder="e.g. 20"
              suffix="m2"
            />
          </v-col>
        </v-row>

        <!--</editor-fold>-->

        <!--<editor-fold desc="Engines">-->
        <v-subheader class="justify-space-between pr-0 mt-6">
          Engines
          <v-btn
            text
            color="grey"
            @click="addEngine"
          >
            <v-icon
              small
              left
            >
              add
            </v-icon>
            Add
          </v-btn>
        </v-subheader>
        <v-autocomplete
          v-for="(engine, i) in yachtModelDirty.engines"
          :key="i"
          v-model="engine.id"
          outlined
          placeholder="Select Engines.."
          single-line
          hide-details
          :items="yachtEngines.data"
          item-value="id"
        >
          <v-btn
            slot="prepend"
            icon
            @click.native.stop="removeEngine(i)"
          >
            <v-icon color="red">
              close
            </v-icon>
          </v-btn>
          <template
            slot="item"
            slot-scope="{ item }"
          >
            <v-list-item-content>
              <v-list-item-title>{{ item.manufacturer }} {{ item.model }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="grey--text">
              {{ item.power }} HP
            </v-list-item-action>
          </template>
          <template
            slot="selection"
            slot-scope="{ item }"
          >
            {{ item.manufacturer }} {{ item.model }} <span class="grey--text ml-2">
              ({{ item.power }} HP)
            </span>
          </template>
        </v-autocomplete>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Water Tanks">-->
        <v-subheader class="justify-space-between pr-0 mt-6">
          Water Tanks
          <v-btn
            text
            color="grey"
            @click="addWaterTank"
          >
            <v-icon
              small
              left
            >
              add
            </v-icon>
            Add
          </v-btn>
        </v-subheader>
        <v-row>
          <v-col
            v-for="(waterTank, i) in yachtModelDirty.water_tanks"
            :key="i"
            style="flex: 33.3% 0 0"
          >
            <v-text-field
              v-model="yachtModelDirty.water_tanks[i]"
              v-validate="'numeric'"
              :name="`water_tanks_${i}`"
              data-vv-as="water tank"
              :error-messages="veeErrors.collect(`water_tanks_${i}`)"
              outlined
              hide-details
              suffix="l"
              :label="'Water Tank #' + (i+1)"
              placeholder="e.g. 1500"
            >
              <v-btn
                slot="prepend"
                icon
                @click="removeWaterTank(i)"
              >
                <v-icon color="red">
                  close
                </v-icon>
              </v-btn>
            </v-text-field>
          </v-col>
        </v-row>

        <!--</editor-fold>-->

        <!--<editor-fold desc="Fuel Tanks">-->
        <v-subheader class="justify-space-between pr-0 mt-6">
          Fuel Tanks
          <v-btn
            text
            color="grey"
            @click="addFuelTank"
          >
            <v-icon
              small
              left
            >
              add
            </v-icon>
            Add
          </v-btn>
        </v-subheader>
        <v-row>
          <v-col
            v-for="(fuelTank, i) in yachtModelDirty.fuel_tanks"
            :key="i"
            style="flex: 33.3% 0 0"
          >
            <v-text-field
              v-model="yachtModelDirty.fuel_tanks[i]"
              v-validate="'numeric'"
              :name="`fuel_tanks_${i}`"
              data-vv-as="fuel tank"
              :error-messages="veeErrors.collect(`fuel_tanks_${i}`)"
              outlined
              hide-details
              suffix="l"
              :label="'Fuel Tank #' + (i+1)"
              placeholder="e.g. 1500"
            >
              <v-btn
                slot="prepend"
                icon
                @click="removeFuelTank(i)"
              >
                <v-icon color="red">
                  close
                </v-icon>
              </v-btn>
            </v-text-field>
          </v-col>
        </v-row>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Photos">-->
        <v-subheader class="mt-6">
          Photos
          <span class="caption grey--text ml-2">
            Upload boat photos
          </span>
        </v-subheader>
        <dropzone
          dropzone-id="dz1"
          :initial-files="photos"
          accepted-file-types="image/*"
          :entity-type="fileEntityType"
          :category="fileCategoryPhoto"
        />
        <!--</editor-fold>-->

        <!--<editor-fold desc="Documents">-->
        <v-subheader class="mt-6">
          Documents
          <span class="caption grey--text ml-2">
            For e.g. upload official boat prospects, documents, catalogues, specifications
          </span>
        </v-subheader>
        <dropzone
          dropzone-id="dz2"
          :initial-files="documents"
          :entity-type="fileEntityType"
          :category="fileCategoryDocument"
        />
        <!--</editor-fold>-->
      </div>
    </v-card-text>

    <template slot="dialogActions">
      <v-btn
        v-if="yachtModelDirty.id && $can('delete', 'YachtModel')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Yacht Model?"
      @confirmed="deleteYachtModel"
    />
  </content-dialog>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import { mapState } from 'vuex'
  import File from '@/models/File'
  import YachtModel from '@/models/YachtModel'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import ContentDialog from '@/components/helpers/Dialog'
  import CurrencyInput from '@/components/helpers/CurrencyInput'
  import YearSelect from '@/components/helpers/YearSelect'
  import Dropzone from '@/components/helpers/Dropzone'
  import CountriesList from '@/components/settings/yachtModels/YachtModelCountryList'
  import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
  import validationScrollMixin from '@/mixins/validationScrollMixin'

  export default {
    name: 'YachtModelEditDialog',
    components: {
      CurrencyInput,
      ContentDialog,
      Dropzone,
      ConfirmDeleteDialog,
      CountriesList,
      YearSelect
    },
    mixins: [deleteConfirmationMixin, validationScrollMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      yachtModel: {
        type: Object,
        default: () => YachtModel.emptyModel
      }
    },
    data() {
      return {
        yachtModelDirty: cloneDeep(this.yachtModel),
        submitting: false,
        automaticMarkup: {
          percentage: 0,
          type: 'margin', // margin or addon
          purchaseActive: false,
          saleActive: false
        }
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      purchasePriceFactor() {
        if (this.automaticMarkup.type === 'margin') return (100 - this.automaticMarkup.percentage) / 100

        return 100 / (100 + Number(this.automaticMarkup.percentage))
      },
      purchasePriceDiffers() {
        if (!this.yachtModelDirty.sale_price || !this.yachtModelDirty.purchase_price || this.purchasePriceFactor === 1) return false

        return Math.abs(1 - (Number(this.yachtModelDirty.sale_price * this.purchasePriceFactor) / this.yachtModelDirty.purchase_price)) > 0.001
      },
      fileCategoryPhoto() {
        return File.CATEGORY_PHOTO
      },
      fileCategoryDocument() {
        return File.CATEGORY_DOCUMENT
      },
      fileEntityType() {
        return File.TYPE_YACHT_MODEL
      },
      countries() {
        const countries = cloneDeep(this.$store.getters['settings/charteringCountries'])

        countries.data.map(c => {
          c.pivot = {
            cost_cdr: null,
            currency: this.yachtModelDirty.currency,
            yearly_income: null,
            yearly_expense: null
          }
        })

        return countries
      },
      documents() {
        return this.yachtModelDirty.files.filter(f => f.category === File.CATEGORY_DOCUMENT)
      },
      photos() {
        return this.yachtModelDirty.files.filter(f => f.category === File.CATEGORY_PHOTO)
      },
      validationRequired() {
        return this.yachtModelDirty.status === this.yachtModelStatuses.STATUS_ACTIVE
      },
      canActivateYachtModel() {
        return this.$can('edit-purchase-price', 'YachtModel') &&
          this.$can('edit-sale-price', 'YachtModel') &&
          this.$can('set-income-costs', 'YachtModel') &&
          this.$can('set-cdr-cost', 'YachtModel')
      },
      ...mapState('settings', ['yachtManufacturers', 'yachtEngines', 'yachtTypes'])
    },
    watch: {
      yachtModel: function (val) {
        this.yachtModelDirty = cloneDeep(val)
        this.$store.dispatch('clearFiles')
      },
      'yachtModelDirty.status': function (value) {
        if (value === this.yachtModelStatuses.STATUS_DRAFT) {
          this.yachtModelDirty.is_sales_active = false
        }
      }
    },
    created() {
      this.currencyTypes = YachtModel.CURRENCY_TYPES
      this.yachtModelStatuses = YachtModel
    },
    methods: {
      async submit() {
        let validate = await this.$validator.validate()

        if (!validate) {
          this.showAlertAndScrollToFirstError()
          return
        }

        this.save()
      },
      async save() {
        this.submitting = true

        let data = {
          name: this.yachtModelDirty.name,
          label: this.yachtModelDirty.label,
          status: this.yachtModelDirty.status,
          is_sales_active: this.yachtModelDirty.status === this.yachtModelStatuses.STATUS_ACTIVE ? this.yachtModelDirty.is_sales_active : false,
          is_partners_visible: this.yachtModelDirty.is_partners_visible,
          model_year: this.yachtModelDirty.model_year,
          purchase_price: this.yachtModelDirty.purchase_price,
          sale_price: this.yachtModelDirty.sale_price,
          currency: this.yachtModelDirty.currency,
          yacht_manufacturer_id: this.yachtModelDirty.yacht_manufacturer_id,
          yacht_type_id: this.yachtModelDirty.yacht_type_id,
          water_tanks: this.yachtModelDirty.water_tanks.filter(wt => !!wt),
          fuel_tanks: this.yachtModelDirty.fuel_tanks.filter(wt => !!wt),
          length: this.yachtModelDirty.length,
          berth_count: this.yachtModelDirty.berth_count,
          beam: this.yachtModelDirty.beam,
          draught: this.yachtModelDirty.draught,
          mast_height: this.yachtModelDirty.mast_height,
          displacement: this.yachtModelDirty.displacement,
          cabin_count: this.yachtModelDirty.cabin_count,
          crew_cabin_count: this.yachtModelDirty.crew_cabin_count,
          crew_berth_count: this.yachtModelDirty.crew_berth_count,
          toilette_count: this.yachtModelDirty.toilette_count,
          steering_wheel_count: this.yachtModelDirty.steering_wheel_count,
          main_sail_area: this.yachtModelDirty.main_sail_area,
          description: this.yachtModelDirty.description,
          countries: this.yachtModelDirty.countries,
          engines: this.yachtModelDirty.engines.filter(e => !!e.id),
          files: {
            data: this.$store.state.dropzone.files.map(file => {
              return file.response
            }),
            removedFileIds: this.$store.state.dropzone.removedIds
          },
          // in case creating a duplicated model
          equipment_packs: this.yachtModelDirty.equipment_packs.map(ep => ({
            id: ep.id,
            default: !!ep.default
          }))
        }

        try {
          let yachtModel = await YachtModel.api.save(data, this.yachtModelDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht model saved',
            'color': 'success'
          })
          this.$store.dispatch('clearFiles')
          this.$emit(this.yachtModelDirty.id ? 'yachtModel:updated' : 'yachtModel:created', yachtModel)

          this.$validator.reset()

          this.modalVisible = false
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      async deleteYachtModel() {
        this.deleting = true

        try {
          await YachtModel.api.destroy(this.yachtModelDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht model deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('yachtModel:deleted', this.yachtModelDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      },
      removeSelectedCountry(id) {
        this.yachtModelDirty.countries.splice(this.yachtModelDirty.countries.indexOf(c => c.id === id), 1)
      },
      addEngine() {
        let lastElement = this.yachtModelDirty.engines[this.yachtModelDirty.engines.length - 1]

        this.yachtModelDirty.engines.push({ id: lastElement ? lastElement.id : null })
      },
      removeEngine(index) {
        if (index === 0 && this.yachtModelDirty.engines.length === 1) {
          this.yachtModelDirty.engines = [{ id: null }]
        } else {
          this.yachtModelDirty.engines.splice(index, 1)
        }
      },
      addWaterTank() {
        let lastElement = this.yachtModelDirty.water_tanks[this.yachtModelDirty.water_tanks.length - 1]

        this.yachtModelDirty.water_tanks.push(lastElement || null)
      },
      removeWaterTank(index) {
        if (index === 0 && this.yachtModelDirty.water_tanks.length === 1) {
          this.yachtModelDirty.water_tanks = [null]
        } else {
          this.yachtModelDirty.water_tanks.splice(index, 1)
        }
      },
      addFuelTank() {
        let lastElement = this.yachtModelDirty.fuel_tanks[this.yachtModelDirty.fuel_tanks.length - 1]

        this.yachtModelDirty.fuel_tanks.push(lastElement || null)
      },
      removeFuelTank(index) {
        if (index === 0 && this.yachtModelDirty.fuel_tanks.length === 1) {
          this.yachtModelDirty.fuel_tanks = [null]
        } else {
          this.yachtModelDirty.fuel_tanks.splice(index, 1)
        }
      },
      calculateSalePrice() {
        if (!this.yachtModelDirty.purchase_price || !this.automaticMarkup.purchaseActive) return

        this.yachtModelDirty.sale_price = Number(Number(this.yachtModelDirty.purchase_price / this.purchasePriceFactor).toFixed(0))
      },
      calculatePurchasePrice() {
        if (!this.yachtModelDirty.sale_price || !this.automaticMarkup.saleActive) return

        this.yachtModelDirty.purchase_price = Number(Number(this.yachtModelDirty.sale_price * this.purchasePriceFactor).toFixed(0))
      }
    }
  }
</script>

<style>
  .v-text-field.v-text-field--enclosed .v-input__prepend-outer {
    margin: 0;
  }
</style>
